import { useEffect, useMemo, useState } from "react";
import type { UnitWeight } from "../types";
import { kgToDisplay } from "../types";

interface AnimatedLineChartProps {
  readonly startKg: number;
  readonly goalKg: number;
  readonly weeks: number;
  readonly unit: UnitWeight;
}

const WIDTH = 320;
const HEIGHT = 140;
const PAD_LEFT = 42;
const PAD_RIGHT = 12;
const PAD_TOP = 16;
const PAD_BOTTOM = 20;
const MAX_POINTS = 24;
const GRID_FRACTIONS = [0.25, 0.5, 0.75] as const;

interface Pt {
  readonly x: number;
  readonly y: number;
}

/** Catmull-Rom spline converted to cubic bezier segments (tension 1, clamped ends). */
function buildSmoothPath(points: readonly Pt[]): { d: string; segments: Array<[Pt, Pt, Pt, Pt]> } {
  const segments: Array<[Pt, Pt, Pt, Pt]> = [];
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    segments.push([p1, c1, c2, p2]);
    d += ` C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p2.x} ${p2.y}`;
  }
  return { d, segments };
}

function cubicAt(segment: [Pt, Pt, Pt, Pt], t: number): Pt {
  const [p0, c1, c2, p1] = segment;
  const mt = 1 - t;
  return {
    x: mt * mt * mt * p0.x + 3 * mt * mt * t * c1.x + 3 * mt * t * t * c2.x + t * t * t * p1.x,
    y: mt * mt * mt * p0.y + 3 * mt * mt * t * c1.y + 3 * mt * t * t * c2.y + t * t * t * p1.y,
  };
}

function approximateLength(segments: ReadonlyArray<[Pt, Pt, Pt, Pt]>): number {
  let total = 0;
  for (const segment of segments) {
    let prev = segment[0];
    for (let s = 1; s <= 8; s += 1) {
      const p = cubicAt(segment, s / 8);
      total += Math.hypot(p.x - prev.x, p.y - prev.y);
      prev = p;
    }
  }
  return total;
}

/**
 * Weight-trend projection: smoothed curve that draws itself in via stroke-dashoffset,
 * gradient area fill that fades in afterwards, dashed goal line, and a glowing end dot.
 */
export function AnimatedLineChart({ startKg, goalKg, weeks, unit }: AnimatedLineChartProps) {
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const { linePath, areaPath, length, endPoint, startPoint, startY, goalY, baselineY, gridYs } =
    useMemo(() => {
      const safeWeeks = Math.max(1, weeks);
      const pointCount = Math.min(safeWeeks + 1, MAX_POINTS);
      const minW = Math.min(startKg, goalKg);
      const maxW = Math.max(startKg, goalKg);
      const span = Math.max(maxW - minW, 1);
      const head = span * 0.1;
      const plotW = WIDTH - PAD_LEFT - PAD_RIGHT;
      const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
      const base = HEIGHT - PAD_BOTTOM;
      const yFor = (w: number) =>
        PAD_TOP + (1 - (w - (minW - head)) / (span + head * 2)) * plotH;
      const xFor = (i: number) => PAD_LEFT + (i / (pointCount - 1)) * plotW;

      const points: Pt[] = [];
      for (let i = 0; i < pointCount; i += 1) {
        const t = i / (pointCount - 1);
        const eased = 0.55 * t + 0.45 * t * t * (3 - 2 * t);
        points.push({ x: xFor(i), y: yFor(startKg + (goalKg - startKg) * eased) });
      }

      const { d, segments } = buildSmoothPath(points);
      const last = points[points.length - 1];
      const first = points[0];
      return {
        linePath: d,
        areaPath: `${d} L ${last.x} ${base} L ${first.x} ${base} Z`,
        length: Math.max(approximateLength(segments), 1),
        endPoint: last,
        startPoint: first,
        startY: yFor(startKg),
        goalY: yFor(goalKg),
        baselineY: base,
        gridYs: GRID_FRACTIONS.map((f) => PAD_TOP + f * plotH),
      };
    }, [startKg, goalKg, weeks]);

  const startDisplay = kgToDisplay(startKg, unit);
  const goalDisplay = kgToDisplay(goalKg, unit);

  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-border bg-surface p-3">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`Projected weight from ${startDisplay} to ${goalDisplay} ${unit} over ${weeks} weeks`}
      >
        <defs>
          <linearGradient id="ob-line-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.28} />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {gridYs.map((y) => (
          <line
            key={y}
            x1={PAD_LEFT}
            y1={y}
            x2={WIDTH - PAD_RIGHT}
            y2={y}
            stroke="var(--color-border)"
            strokeWidth={1}
            opacity={0.6}
          />
        ))}
        <line
          x1={PAD_LEFT}
          y1={baselineY}
          x2={WIDTH - PAD_RIGHT}
          y2={baselineY}
          stroke="var(--color-border)"
          strokeWidth={1}
        />

        <line
          x1={PAD_LEFT}
          y1={goalY}
          x2={WIDTH - PAD_RIGHT}
          y2={goalY}
          stroke="var(--color-accent)"
          strokeWidth={1.5}
          strokeDasharray="5 4"
          opacity={0.7}
        />

        <path
          d={areaPath}
          fill="url(#ob-line-area)"
          style={{
            opacity: drawn ? 1 : 0,
            transition: "opacity 900ms ease-out 1.1s",
          }}
        />
        <path
          d={linePath}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={length}
          strokeDashoffset={drawn ? 0 : length}
          style={{ transition: "stroke-dashoffset 1.4s ease-out" }}
        />

        <circle cx={startPoint.x} cy={startPoint.y} r={3.5} fill="var(--color-accent-dark)" />
        <g
          style={{
            opacity: drawn ? 1 : 0,
            transition: "opacity 500ms ease-out 1.2s",
          }}
        >
          <circle
            cx={endPoint.x}
            cy={endPoint.y}
            r={10}
            fill="var(--color-accent)"
            className="ob-glow-dot"
          />
          <circle cx={endPoint.x} cy={endPoint.y} r={4.5} fill="var(--color-accent)" />
        </g>

        <text
          x={PAD_LEFT - 6}
          y={startY + 3.5}
          textAnchor="end"
          fontSize={10}
          fill="var(--color-text-muted)"
        >
          {startDisplay} {unit}
        </text>
        <text
          x={PAD_LEFT - 6}
          y={goalY + 3.5}
          textAnchor="end"
          fontSize={10}
          fontWeight={600}
          fill="var(--color-accent)"
        >
          {goalDisplay} {unit}
        </text>
        <text x={PAD_LEFT} y={HEIGHT - 5} textAnchor="start" fontSize={10} fill="var(--color-text-muted)">
          Now
        </text>
        <text
          x={WIDTH - PAD_RIGHT}
          y={HEIGHT - 5}
          textAnchor="end"
          fontSize={10}
          fill="var(--color-text-muted)"
        >
          Week {weeks}
        </text>
      </svg>
    </div>
  );
}
